import { format, parseISO, differenceInYears, differenceInDays } from "date-fns";

/**
 * Yerel tarih ve saati ISO formatında string olarak döndürür
 * @returns ISO formatında tarih string'i (örn: "2025-12-01T14:27:59")
 */
export const getLocalISO = (): string => {
  const date = new Date();
  return format(date, "yyyy-MM-dd'T'HH:mm:ss");
};

/**
 * Date objesini yerel ISO formatında string'e çevirir
 * @param date - Date objesi veya null/undefined
 * @returns ISO formatında tarih string'i (örn: "2025-12-01T14:27:59") veya boş string
 */
export const toLocalISO = (date: Date | null | undefined): string => {
  if (!date) return "";

  try {
    return format(date, "yyyy-MM-dd'T'HH:mm:ss");
  } catch (error) {
    console.warn("Date formatting error:", error);
    return "";
  }
};

export const safePtime = (value: any): string | null => {
  if (!value) return null;

  const iso = toLocalISO(value);
  if (!iso || iso.trim() === "") return null;

  return iso;
};

/**
 * Date objesini yerel tarih formatında string'e çevirir (saat bilgisi olmadan)
 * @param date - Date objesi veya null/undefined
 * @returns YYYY-MM-DD formatında tarih string'i
 */
export const toLocalDate = (date: Date | null | undefined): string => {
  if (!date) return "";

  try {
    return format(date, "yyyy-MM-dd");
  } catch (error) {
    console.warn("Date formatting error:", error);
    return "";
  }
};

/**
 * ISO tarih string'ini formatlanmış tarih string'ine çevirir
 * @param dateString - ISO formatında tarih string'i (örn: "2025-12-01T14:27:59")
 * @param dateFormat - Tarih formatı (varsayılan: "dd.MM.yyyy HH:mm")
 * @returns Formatlanmış tarih string'i veya boş string
 */
export const formatDateTime = (
  dateString: string | null | undefined,
  dateFormat: string = "dd.MM.yyyy HH:mm"
): string => {
  if (!dateString) return "";

  try {
    const date = parseISO(dateString);
    return format(date, dateFormat);
  } catch (error) {
    console.warn("Date parsing error:", error);
    return dateString;
  }
};

/**
 * DataGrid için valueFormatter fonksiyonu
 * @param value - Grid cell'den gelen değer
 * @param dateFormat - Tarih formatı (varsayılan: "dd.MM.yyyy HH:mm")
 * @returns Formatlanmış tarih string'i
 */
export const dateTimeFormatter = (
  value: string | null | undefined,
  dateFormat: string = "dd.MM.yyyy HH:mm"
): string => {
  return formatDateTime(value, dateFormat);
};

export const formatLongDate = (date: Date | string | null | undefined): string => {
  if (!date) return "";

  try {
    const d = typeof date === "string" ? new Date(date) : date;

    return new Intl.DateTimeFormat(undefined, {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(d);
  } catch (error) {
    console.warn("Date format error:", error);
    return "";
  }
};

/**
 * Date objesini yerel saat diliminde (offset olmadan) ISO formatında string'e çevirir
 * @param date - Date objesi veya null/undefined
 * @returns ISO formatında tarih string'i (örn: "2025-12-31T09:24:34.366") veya boş string
 * Enes Özbuğanlı 31.12.2025 Boy kilo için yazıldı.
 */
export const toLocalISOWithMillis = (date: Date | null | undefined): string => {
  if (!date) return "";

  try {
    return format(date, "yyyy-MM-dd'T'HH:mm:ss.SSS");
  } catch (error) {
    console.warn("Date formatting error:", error);
    return "";
  }
};

export const calculateAge = (birthDateInput: Date | string | null | undefined): { years: number; totalDays: number } | null => {
  if (!birthDateInput) return null;

  try {
    const birthDate = typeof birthDateInput === "string" ? new Date(birthDateInput) : birthDateInput;
    const today = new Date();

    const years = differenceInYears(today, birthDate);
    const totalDays = differenceInDays(today, birthDate);

    return { years, totalDays };
  } catch (error) {
    console.warn("Age calculation error:", error);
    return null;
  }
};

